import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Row,
  Col,
  Badge,
  Form,
  OverlayTrigger,
  Popover,
  Tooltip,
} from "react-bootstrap";
import { FiInfo } from "react-icons/fi";
import {
  Calendar,
  Search,
  Box,
  Truck,
  CheckCircle,
  Clock,
  FileText,
  Layers,
  X,
  ArrowUpDown,
} from "lucide-react";

// Definiamo la gerarchia degli stati logistici per gestire il filtro progressivo e l'ordinamento
const STATUS_HIERARCHY = {
  ORDINATO: 1,
  RIMESSO: 2,
  "ENTRATO IN MAGAZZINO": 3,
  "BEM INVIATA": 4,
  "BEM COMPLETATA": 5,
  "INVIO ODP/ODPV": 6,
  "COMPLETAMENTO ODP/ODPV": 7,
};

// Funzione di utility per calcolare i giorni lavorativi effettivi (esclusi Sabato e Domenica)
const getWorkingDaysCount = (startDate, endDate) => {
  let start = new Date(startDate);
  let end = new Date(endDate);

  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  if (start > end) return 0;

  let workingDays = 0;
  while (start <= end) {
    const day = start.getDay();
    if (day !== 0 && day !== 6) {
      // 0 = Domenica, 6 = Sabato
      workingDays++;
    }
    start.setDate(start.getDate() + 1);
  }
  return workingDays;
};

const calculateStatus = (order) => {
  if (order["Data Completamento ODPV"]) return "COMPLETAMENTO ODP/ODPV";
  if (order["Data Invio ODPV"]) return "INVIO ODP/ODPV";
  if (order["bem_data_copletamento"]) return "BEM COMPLETATA";
  if (order["bem_data_invio"] || order["id_bem"]) return "BEM INVIATA";
  if (order["data_entrata"] || order["numero_entrata"])
    return "ENTRATO IN MAGAZZINO";
  if (order["Data Rimesso"] || order["numero_rimesso"]) return "RIMESSO";
  return "ORDINATO";
};

const SalesChart = () => {
  const [urgentOrders, setUrgentOrders] = useState([]);
  const [entrateData, setEntrateData] = useState([]); // Stato per memorizzare la lista ENTRATE per i giorni spedizione
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("TUTTI");
  const [sortBy, setSortBy] = useState("DOC_ASC");
  const [tempDates, setTempDates] = useState({ start: "", end: "" });
  const [appliedDates, setAppliedDates] = useState({ start: "", end: "" });
  const [showModal, setShowModal] = useState(false);
  const [showFullscreenModal, setShowFullscreenModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    fetch("ordini_urgenti.json")
      .then((res) => res.json())
      .then((json) => {
        const ordiniRaw = json?.STATO_ORDINI?.flat() || [];
        const entrateRaw = json?.ENTRATE || [];

        setEntrateData(entrateRaw);

        const mapped = ordiniRaw.map((order) => ({
          ...order,
          calculatedStatus: calculateStatus(order),
        }));

        const sorted = mapped.sort((a, b) =>
          String(a["Num Doc"]).localeCompare(String(b["Num Doc"])),
        );
        setUrgentOrders(sorted);
      })
      .catch((err) => console.error("Errore nel caricamento dati:", err));
  }, []);

  const getStatusStyle = (stato) => {
    const map = {
      ORDINATO: { bg: "#f1f3f5", color: "#495057", icon: <Clock size={12} /> },
      RIMESSO: { bg: "#f3f0ff", color: "#6741d9", icon: <Layers size={12} /> },
      "ENTRATO IN MAGAZZINO": {
        bg: "#e3fafc",
        color: "#0c8599",
        icon: <Box size={12} />,
      },
      "BEM INVIATA": {
        bg: "#fff9db",
        color: "#f08000",
        icon: <FileText size={12} />,
      },
      "BEM COMPLETATA": {
        bg: "#fff0f6",
        color: "#d6336c",
        icon: <CheckCircle size={12} />,
      },
      "INVIO ODP/ODPV": {
        bg: "#f3f0ff",
        color: "#6741d9",
        icon: <Truck size={12} />,
      },
      "COMPLETAMENTO ODP/ODPV": {
        bg: "#edf2ff",
        color: "#364fc7",
        icon: <CheckCircle size={12} />,
      },
    };

    return (
      map[stato] || {
        bg: "#f8f9fa",
        color: "#495057",
        icon: <Layers size={12} />,
      }
    );
  };

  const getDelayBadge = (order, statoCorrente, entrateList = []) => {
    // 1. Se l'ordine è completato, mostra direttamente "EVASO"
    if (statoCorrente === "COMPLETAMENTO ODP/ODPV") {
      return (
        <Badge
          bg="secondary"
          style={{ fontSize: "0.65rem", padding: "6px 10px" }}
        >
          EVASO
        </Badge>
      );
    }

    const today = new Date();
    const dataOrdineRaw = order["Data Ordine"]?.split(" ")[0];
    const dataConfermaRaw = order["Data Conferma"]?.split(" ")[0];
    const dataEffettivaRaw = order["Data Effettiva"]?.split(" ")[0];

    // Scegliamo la data di scadenza primaria: Data Effettiva, altrimenti Data Conferma
    const dataTargetRaw = dataEffettivaRaw || dataConfermaRaw;

    if (dataTargetRaw) {
      const dataTarget = new Date(dataTargetRaw);
      // Se oggi è oltre la data stabilita -> RITARDO
      if (today.setHours(0, 0, 0, 0) > dataTarget.setHours(0, 0, 0, 0)) {
        return (
          <Badge
            bg="danger"
            style={{ fontSize: "0.65rem", padding: "6px 10px" }}
          >
            IN RITARDO
          </Badge>
        );
      }
      return (
        <Badge
          bg="success"
          style={{ fontSize: "0.65rem", padding: "6px 10px" }}
        >
          IN ORARIO
        </Badge>
      );
    }

    // 2. FALLBACK: Se mancano conferma ed effettiva, andiamo su "ENTRATE" per i Giorni Spedizione del Fornitore
    if (dataOrdineRaw) {
      const infoEntrata = entrateList.find(
        (e) => String(e["ID Fornitore"]) === String(order["ID Fornitore"]),
      );

      const giorniSpedizione = infoEntrata
        ? parseInt(infoEntrata["Giorni Spedizione"] || 0, 10)
        : 0;

      if (giorniSpedizione > 0) {
        const giorniLavorativiPassati = getWorkingDaysCount(
          dataOrdineRaw,
          today,
        );

        if (giorniLavorativiPassati > giorniSpedizione) {
          return (
            <Badge
              bg="danger"
              style={{ fontSize: "0.65rem", padding: "6px 10px" }}
            >
              IN RITARDO
            </Badge>
          );
        }
      }
    }

    return (
      <Badge bg="success" style={{ fontSize: "0.65rem", padding: "6px 10px" }}>
        IN ORARIO
      </Badge>
    );
  };

  const filteredOrders = urgentOrders.filter((order) => {
    const orderDate = order["Data Ordine"]
      ? order["Data Ordine"].split(" ")[0]
      : "";
    const s = searchTerm.toLowerCase();

    const matchesSearch =
      order["Num Doc"]?.toString().toLowerCase().includes(s) ||
      order["Articolo"]?.toString().toLowerCase().includes(s) ||
      order["Ragione sociale"]?.toString().toLowerCase().includes(s) ||
      order["Rag Soc Cli"]?.toString().toLowerCase().includes(s);

    let matchesStatus = true;
    if (statusFilter !== "TUTTI") {
      const currentLevel = STATUS_HIERARCHY[order.calculatedStatus] || 0;
      const filterLevel = STATUS_HIERARCHY[statusFilter] || 0;
      matchesStatus = currentLevel >= filterLevel;
    }

    const matchesStart =
      appliedDates.start === "" || orderDate >= appliedDates.start;
    const matchesEnd = appliedDates.end === "" || orderDate <= appliedDates.end;

    return matchesSearch && matchesStatus && matchesStart && matchesEnd;
  });

  const sortedAndFilteredOrders = [...filteredOrders].sort((a, b) => {
    if (sortBy === "ALPHA_ASC") {
      return String(a["Articolo"] || "").localeCompare(
        String(b["Articolo"] || ""),
      );
    }
    if (sortBy === "DATE_DESC") {
      const dateA = a["Data Ordine"]
        ? new Date(a["Data Ordine"].split(" ")[0])
        : new Date(0);
      const dateB = b["Data Ordine"]
        ? new Date(b["Data Ordine"].split(" ")[0])
        : new Date(0);
      return dateB - dateA;
    }
    if (sortBy === "STATUS_ASC") {
      const levelA = STATUS_HIERARCHY[a.calculatedStatus] || 0;
      const levelB = STATUS_HIERARCHY[b.calculatedStatus] || 0;
      return levelA - levelB;
    }
    return String(a["Num Doc"]).localeCompare(String(b["Num Doc"]));
  });

  const renderTooltipSpiegazione = (props) => (
    <Tooltip
      id="info-ordini-tooltip"
      {...props}
      style={{ ...props.style, fontSize: "0.85rem" }}
    >
      Il filtro di stato mostra tutti gli articoli che hanno raggiunto o
      superato la fase selezionata.
    </Tooltip>
  );

  const renderTimelineStep = (
    label,
    dateString,
    infoAggiuntiva = "",
    isCurrentStatus = false,
  ) => {
    const isDone = dateString && dateString !== "null" && dateString !== null;

    return (
      <Col className="text-center px-1">
        <div
          style={{
            fontSize: "0.6rem",
            fontWeight: isCurrentStatus ? "900" : "800",
            color: isCurrentStatus ? "#dc3545" : isDone ? "#0d6efd" : "#adb5bd",
            textTransform: "uppercase",
            marginBottom: "4px",
            minHeight: "24px",
          }}
        >
          {label} {isCurrentStatus}
        </div>
        {infoAggiuntiva && (
          <div
            style={{ fontSize: "0.55rem", color: "#6c757d", minHeight: "14px" }}
          >
            {isDone ? infoAggiuntiva : ""}
          </div>
        )}
        <div
          style={{
            height: "4px",
            backgroundColor: isCurrentStatus
              ? "#dc3545"
              : isDone
                ? "#0d6efd"
                : "#e9ecef",
            borderRadius: "2px",
            marginTop: "8px",
            marginBottom: "8px",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "-5px",
              left: "50%",
              width: "14px",
              height: "14px",
              borderRadius: "50%",
              backgroundColor: isCurrentStatus
                ? "#dc3545"
                : isDone
                  ? "#0d6efd"
                  : "#dee2e6",
              transform: "translateX(-50%)",
              border: isCurrentStatus ? "2px solid white" : "none",
              boxShadow: isCurrentStatus
                ? "0 0 8px rgba(220, 53, 69, 0.6)"
                : "none",
            }}
          ></div>
        </div>
        <div
          style={{
            fontSize: "0.65rem",
            color: isCurrentStatus ? "#dc3545" : isDone ? "#212529" : "#dee2e6",
            fontWeight: isDone ? "600" : "400",
          }}
        >
          {isDone
            ? new Date(dateString.replace(/-/g, "/")).toLocaleDateString(
                "it-IT",
              )
            : "--/--"}
        </div>
      </Col>
    );
  };

  const renderFilters = () => (
    <div className="d-flex gap-2 align-items-center">
      <OverlayTrigger
        trigger="click"
        placement="bottom"
        rootClose
        overlay={
          <Popover
            className="shadow-lg border-0 p-3"
            style={{ borderRadius: "12px", zIndex: 1060 }}
          >
            <Form.Group className="mb-2">
              <Form.Label className="small fw-bold">
                Intervallo Ordine
              </Form.Label>
              <Form.Control
                type="date"
                size="sm"
                className="mb-2"
                value={tempDates.start}
                onChange={(e) =>
                  setTempDates({ ...tempDates, start: e.target.value })
                }
              />
              <Form.Control
                type="date"
                size="sm"
                value={tempDates.end}
                onChange={(e) =>
                  setTempDates({ ...tempDates, end: e.target.value })
                }
              />
            </Form.Group>
            <Button
              variant="primary"
              size="sm"
              className="w-100"
              onClick={() => {
                setAppliedDates(tempDates);
                document.body.click();
              }}
            >
              Applica
            </Button>
          </Popover>
        }
      >
        <Button
          variant="light"
          className="border-0 shadow-sm"
          style={{ borderRadius: "10px", padding: "8px 12px" }}
        >
          <Calendar size={16} className="text-primary" />
        </Button>
      </OverlayTrigger>

      <Form.Select
        size="sm"
        className="border-0 shadow-sm"
        style={{
          borderRadius: "10px",
          width: "220px",
          height: "38px",
          fontSize: "0.8rem",
          fontWeight: "600",
        }}
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
      >
        <option value="TUTTI">Tutti gli stati ({filteredOrders.length})</option>
        <option value="ORDINATO">ORDINATO</option>
        <option value="RIMESSO">RIMESSO</option>
        <option value="ENTRATO IN MAGAZZINO">ENTRATO IN MAGAZZINO</option>
        <option value="BEM INVIATA">BEM INVIATA</option>
        <option value="BEM COMPLETATA">BEM COMPLETATA</option>
        <option value="INVIO ODP/ODPV">INVIO ODP/ODPV</option>
        <option value="COMPLETAMENTO ODP/ODPV">COMPL. ODP/ODPV</option>
      </Form.Select>

      <div className="position-relative">
        <Search
          size={16}
          className="position-absolute"
          style={{
            left: "12px",
            top: "50%",
            transform: "translateY(-50%)",
            color: "#adb5bd",
          }}
        />
        <Form.Control
          size="sm"
          className="border-0 shadow-sm"
          placeholder="Cerca art., doc, cliente..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            borderRadius: "10px",
            width: "240px",
            paddingLeft: "38px",
            height: "38px",
            fontSize: "0.8rem",
          }}
        />
      </div>
    </div>
  );

  const renderTableContent = () => (
    <Table className="custom-table" responsive="true">
      <thead>
        <tr
          className="text-muted"
          style={{
            fontSize: "0.65rem",
            textTransform: "uppercase",
            letterSpacing: "1px",
          }}
        >
          <th
            onClick={() =>
              setSortBy(sortBy === "ALPHA_ASC" ? "DOC_ASC" : "ALPHA_ASC")
            }
            style={{ cursor: "pointer", userSelect: "none" }}
          >
            Articolo{" "}
            <ArrowUpDown
              size={12}
              className="ms-1"
              style={{ color: sortBy === "ALPHA_ASC" ? "#0d6efd" : "#adb5bd" }}
            />
          </th>
          <th
            onClick={() =>
              setSortBy(sortBy === "DATE_DESC" ? "DOC_ASC" : "DATE_DESC")
            }
            style={{ cursor: "pointer", userSelect: "none" }}
          >
            Data Ord. Fornitore{" "}
            <ArrowUpDown
              size={12}
              className="ms-1"
              style={{ color: sortBy === "DATE_DESC" ? "#0d6efd" : "#adb5bd" }}
            />
          </th>
          <th
            onClick={() =>
              setSortBy(sortBy === "STATUS_ASC" ? "DOC_ASC" : "STATUS_ASC")
            }
            style={{ cursor: "pointer", userSelect: "none" }}
          >
            Stato Calcolato{" "}
            <ArrowUpDown
              size={12}
              className="ms-1"
              style={{ color: sortBy === "STATUS_ASC" ? "#0d6efd" : "#adb5bd" }}
            />
          </th>
          <th className="text-end">Priorità</th>
        </tr>
      </thead>
      <tbody>
        {sortedAndFilteredOrders.map((ordine, index) => {
          const showHeader =
            index === 0 ||
            ordine["Num Doc"] !== sortedAndFilteredOrders[index - 1]["Num Doc"];
          const currentStyle = getStatusStyle(ordine.calculatedStatus);

          return (
            <React.Fragment key={index}>
              {showHeader && (
                <tr className="doc-header-row">
                  <td colSpan={4}>
                    <div
                      className="d-flex align-items-center justify-content-between"
                      style={{ fontSize: "0.75rem", fontWeight: "600" }}
                    >
                      <div>
                        <Badge
                          bg="dark"
                          className="me-2"
                          style={{ fontSize: "0.65rem" }}
                        >
                          Ordine Fornitore {ordine["Num Doc"]}
                        </Badge>
                        <span className="text-dark">
                          <small className="text-muted fw-normal">
                            Fornitore:
                          </small>{" "}
                          {ordine["Ragione sociale"]}
                        </span>
                      </div>
                      <span className="text-dark">
                        <small className="text-muted fw-normal">
                          Destinatario:
                        </small>{" "}
                        {ordine["Rag Soc Cli"]}
                      </span>
                    </div>
                  </td>
                </tr>
              )}

              <tr
                className="item-row shadow-sm"
                onClick={() => {
                  setSelectedOrder(ordine);
                  setShowModal(true);
                }}
              >
                <td>
                  <div
                    className="fw-bold text-primary"
                    style={{ fontSize: "0.8rem" }}
                  >
                    {ordine["Articolo"]}
                  </div>
                </td>
                <td style={{ fontSize: "0.78rem" }}>
                  {ordine["Data Ordine"]
                    ? new Date(
                        ordine["Data Ordine"].split(" ")[0],
                      ).toLocaleDateString("it-IT")
                    : "--/--"}
                </td>
                <td>
                  <div
                    className="status-badge"
                    style={{
                      backgroundColor: currentStyle.bg,
                      color: currentStyle.color,
                      borderRadius: "6px",
                    }}
                  >
                    {currentStyle.icon} {ordine.calculatedStatus}
                  </div>
                </td>
                <td className="text-end">
                  {getDelayBadge(ordine, ordine.calculatedStatus, entrateData)}
                </td>
              </tr>
            </React.Fragment>
          );
        })}
      </tbody>
    </Table>
  );

  const qtaOrdinata = selectedOrder
    ? parseFloat(selectedOrder["Quantita"] || 0)
    : 0;
  const qtaRimessa = selectedOrder
    ? parseFloat(selectedOrder["Qta RIM"] || 0)
    : 0;
  const qtaEvasa = selectedOrder
    ? parseFloat(selectedOrder["Qta EVA"] || 0)
    : 0;
  const qtaDaEvadere = Math.max(0, qtaOrdinata - qtaEvasa - qtaRimessa);
  const currentStatus = selectedOrder ? selectedOrder.calculatedStatus : "";

  return (
    <div
      className="bg-white shadow-sm"
      style={{
        borderRadius: "16px",
        maxHeight: "400px",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        border: "1px solid #eee",
      }}
    >
      <div className="p-3 bg-light border-bottom d-flex justify-content-between align-items-center">
        <div
          className="d-flex align-items-center gap-2"
          style={{ cursor: "pointer" }}
        >
          <span
            onClick={() => setShowFullscreenModal(true)}
            style={{
              fontWeight: "800",
              fontSize: "0.75rem",
              color: "#1a1a1a",
              letterSpacing: "1px",
            }}
          >
            MONITORAGGIO ORDINI
          </span>
          <OverlayTrigger placement="bottom" overlay={renderTooltipSpiegazione}>
            <span
              style={{
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              <FiInfo size={15} color="#adb5bd" />
            </span>
          </OverlayTrigger>
        </div>
        {renderFilters()}
      </div>
      <style>{`
        .scroll-area { overflow-y: auto; flex-grow: 1; padding: 15px; background-color: #f8f9fa; }
        .custom-table { border-collapse: separate; border-spacing: 0 6px; }
        .doc-header-row td { background: #e9ecef !important; border: none !important; border-radius: 6px; padding: 8px 15px !important; }
        .item-row { cursor: pointer; transition: all 0.15s; }
        .item-row td { background: white !important; border: none !important; padding: 12px 15px !important; vertical-align: middle; }
        .item-row:hover td { background: #f1f7fe !important; }
        .item-row td:first-child { border-top-left-radius: 10px; border-bottom-left-radius: 10px; }
        .item-row td:last-child { border-top-right-radius: 10px; border-bottom-right-radius: 10px; }
        .status-badge { display: inline-flex; align-items: center; gap: 5px; padding: 5px 10px; font-weight: 700; font-size: 0.65rem; text-transform: uppercase; }
        .row-cols-7 > * { flex: 0 0 auto; width: 14.2857%; }
      `}</style>
      <div className="scroll-area">{renderTableContent()}</div>
      {/* MODALE FULLSCREEN */}
      <Modal
        show={showFullscreenModal}
        onHide={() => setShowFullscreenModal(false)}
        fullscreen={true}
        animation={true}
      >
        <Modal.Body
          className="p-0 d-flex flex-column"
          style={{ background: "#f8f9fa" }}
        >
          <div className="p-3 bg-light border-bottom d-flex justify-content-between align-items-center shadow-sm">
            <span
              style={{
                fontWeight: "800",
                fontSize: "0.9rem",
                color: "#1a1a1a",
                letterSpacing: "1px",
              }}
            >
              MONITORAGGIO ORDINI — VISTA COMPLETA
            </span>
            <div className="d-flex gap-3 align-items-center">
              {renderFilters()}
              <Button
                variant="outline-dark"
                className="d-flex align-items-center gap-1 border-0"
                onClick={() => setShowFullscreenModal(false)}
                style={{ borderRadius: "10px", height: "38px" }}
              >
                <X size={16} /> Chiudi
              </Button>
            </div>
          </div>
          <div className="flex-grow-1 p-4" style={{ overflowY: "auto" }}>
            <div className="bg-white p-3 shadow-sm rounded-4 border">
              {renderTableContent()}
            </div>
          </div>
        </Modal.Body>
      </Modal>
      {/* MODAL DETTAGLIO */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="xl"
        centered
        style={{ zIndex: 1070 }}
        className="p-5"
      >
        <Modal.Body className="p-0">
          {/* HEADER MODALE */}
          <div className="p-4 border-bottom bg-light d-flex justify-content-between align-items-center">
            <div>
              <div
                className="text-primary fw-bold"
                style={{
                  fontSize: "0.65rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Tracciabilità Stato Articolo
              </div>
              <h5 className="mb-0 fw-bold text-dark d-flex align-items-center flex-wrap gap-2">
                <span>{selectedOrder?.Articolo}</span>
                <span
                  className="fw-normal text-muted"
                  style={{ fontSize: "0.9rem" }}
                >
                  - {selectedOrder?.Descrizione}
                </span>
                <span
                  className="fw-normal text-danger fw-bold d-flex align-items-center gap-1"
                  style={{ fontSize: "0.9rem" }}
                >
                  - CONSEGNA CLIENTE:{" "}
                  {(() => {
                    const prevStr = selectedOrder?.["Data Prev Consegna Cli"]
                      ?.toString()
                      .trim();
                    const effStr = selectedOrder?.[
                      " Data Effettiva Consegna Cli"
                    ]
                      ?.toString()
                      .trim();

                    // Se non c'è nessuna delle due date
                    if (!prevStr && !effStr) return "No Data";

                    // Se c'è solo la data prevista
                    if (prevStr && !effStr) {
                      return (
                        <span className="text-danger">
                          {new Date(prevStr).toLocaleDateString("it-IT")}
                        </span>
                      );
                    }

                    // Se c'è solo la data effettiva
                    if (!prevStr && effStr) {
                      return (
                        <span className="text-success">
                          {new Date(effStr).toLocaleDateString("it-IT")}
                        </span>
                      );
                    }

                    // Se ci sono entrambe, calcoliamo qual è la più vecchia e qual è la più recente
                    const prevDate = new Date(prevStr);
                    const effDate = new Date(effStr);

                    const isPrevVecchia = prevDate < effDate;
                    const dataVecchia = isPrevVecchia ? prevDate : effDate;
                    const dataRecente = isPrevVecchia ? effDate : prevDate;

                    return (
                      <>
                        <span
                          style={{
                            textDecoration: "line-through",
                            opacity: 0.6,
                          }}
                          className="text-muted"
                        >
                          {dataVecchia.toLocaleDateString("it-IT")}
                        </span>
                        <span className="text-success ms-1 fw-bold">
                          {dataRecente.toLocaleDateString("it-IT")}
                        </span>
                      </>
                    );
                  })()}
                </span>
              </h5>{" "}
            </div>
            <Button
              variant="outline-secondary"
              size="sm"
              style={{ borderRadius: "8px" }}
              onClick={() => setShowModal(false)}
            >
              Chiudi
            </Button>
          </div>

          {/* TIMELINE */}
          <div className="p-4 bg-white">
            <div className="p-4 bg-light rounded-4 mb-4 border">
              <Row className="g-0 row-cols-7">
                {selectedOrder && (
                  <>
                    {renderTimelineStep(
                      "1. Ordine Fornitore",
                      selectedOrder["Data Ordine"],
                      `Ordine Fornitore: ${selectedOrder["Num Doc"] || ""}`,
                      currentStatus === "ORDINATO",
                    )}
                    {renderTimelineStep(
                      "2. In Rimesso",
                      selectedOrder["Data Rimesso"],
                      selectedOrder["numero_rimesso"]
                        ? `Num: ${selectedOrder["numero_rimesso"]}`
                        : "rimesso elaborato",
                      currentStatus === "RIMESSO",
                    )}
                    {renderTimelineStep(
                      "3. Entrata Magazzino",
                      selectedOrder["Data Entrata Magazzino"] ||
                        selectedOrder["data_entrata"],
                      selectedOrder["Num Entrata Magazzino"] ||
                        selectedOrder["numero_entrata"]
                        ? `Entrata: ${selectedOrder["Num Entrata Magazzino"] || selectedOrder["numero_entrata"]}`
                        : "Entrata:",
                      currentStatus === "ENTRATO IN MAGAZZINO",
                    )}
                    {renderTimelineStep(
                      "4. Invio BEM",
                      selectedOrder["bem_data_invio"],
                      `BEM: ${selectedOrder["id_bem"] || ""}`,
                      currentStatus === "BEM INVIATA",
                    )}
                    {renderTimelineStep(
                      "5. BEM Completata",
                      selectedOrder["bem_data_copletamento"],
                      `BEM: ${selectedOrder["id_bem"] || ""}`,
                      currentStatus === "BEM COMPLETATA",
                    )}
                    {renderTimelineStep(
                      "6. Invio ODP/ODPV",
                      selectedOrder["Data Invio ODPV"],
                      selectedOrder["ODPV"] ? ` ${selectedOrder["ODPV"]}` : "",
                      currentStatus === "INVIO ODP/ODPV",
                    )}
                    {renderTimelineStep(
                      "7. Completato ODP/ODPV",
                      selectedOrder["Data Completamento ODPV"],
                      selectedOrder["ODPV"] ? ` ${selectedOrder["ODPV"]}` : "",
                      currentStatus === "COMPLETAMENTO ODP/ODPV",
                    )}
                  </>
                )}
              </Row>
            </div>

            {/* DETTAGLI ANAGRAFICI E KPI */}
            <Row className="g-3">
              <Col md={6}>
                <div
                  className="p-3 rounded-3 bg-light h-100 border text-muted"
                  style={{ fontSize: "0.8rem" }}
                >
                  <h6
                    className="fw-bold text-dark mb-2"
                    style={{ fontSize: "0.85rem" }}
                  >
                    Riferimenti Anagrafici
                  </h6>
                  <div className="d-flex justify-content-between border-bottom py-2">
                    <span className="fw-bold">Ordine Fornitore:</span>
                    <span className="fw-bold text-dark">
                      {selectedOrder?.["Tipo Doc"]}{" "}
                      <span className="text-light bg-primary rounded-pill p-1 px-2 ms-3">
                        {selectedOrder?.["Num Doc"]}
                      </span>
                    </span>
                  </div>
                  <div className="d-flex justify-content-between border-bottom py-2">
                    <span className="fw-bold">Fornitore:</span>
                    <span className="fw-bold text-dark">
                      {selectedOrder?.["Ragione sociale"]}{" "}
                      <span className="text-light bg-danger rounded-pill p-1 px-2 ms-3">
                        {selectedOrder?.["ID Fornitore"]}
                      </span>
                    </span>
                  </div>

                  {/* DATE FORNITORE AGGIORNATE */}
                  <div className="d-flex justify-content-between border-bottom py-2 bg-secondary px-3">
                    <span className="fw-bold text-white">
                      Data Conf. Fornitore:{" "}
                      {selectedOrder?.["Data Conferma Fornitori"] &&
                      selectedOrder["Data Conferma Fornitori"] !== "(NULL)"
                        ? new Date(
                            selectedOrder["Data Conferma Fornitori"],
                          ).toLocaleDateString("it-IT")
                        : "No Data"}
                    </span>
                    <span className="fw-bold text-white">
                      Data Eff. Fornitore:{" "}
                      {selectedOrder?.["Data Effettiva Fornitori"] &&
                      selectedOrder["Data Effettiva Fornitori"] !== "(NULL)"
                        ? new Date(
                            selectedOrder["Data Effettiva Fornitori"],
                          ).toLocaleDateString("it-IT")
                        : "No Data"}
                    </span>
                  </div>

                  <div className="d-flex justify-content-between border-bottom py-2">
                    <span className="fw-bold">Ordine Cliente:</span>
                    <span className="fw-bold text-dark">
                      {selectedOrder?.["Tipo Ord Cli"]}{" "}
                      <span className="text-light bg-primary rounded-pill p-1 px-2 ms-3">
                        {selectedOrder?.["Num Ord Cli"]}
                      </span>
                    </span>
                  </div>
                  <div className="d-flex justify-content-between py-2">
                    <span className="fw-bold">Cliente:</span>
                    <span className="fw-bold text-dark">
                      {selectedOrder?.["Rag Soc Cli"]}{" "}
                      <span className="text-light bg-danger rounded-pill p-1 px-2 ms-3">
                        {selectedOrder?.["ID Cliente"]}
                      </span>
                    </span>
                  </div>

                  {/* DATE CLIENTI AGGIORNATE */}
                  <div className="d-flex justify-content-between border-bottom py-2 bg-secondary px-3">
                    <span className="fw-bold text-white">
                      Data Conf. Cliente:{" "}
                      {selectedOrder?.["Data Conferma CLIENTI"] &&
                      selectedOrder["Data Conferma CLIENTI"] !== "(NULL)"
                        ? new Date(
                            selectedOrder["Data Conferma CLIENTI"],
                          ).toLocaleDateString("it-IT")
                        : "No Data"}
                    </span>
                    <span className="fw-bold text-white">
                      Data Eff. Cliente:{" "}
                      {selectedOrder?.["Data Effettiva Clienti"] &&
                      selectedOrder["Data Effettiva Clienti"] !== "(NULL)"
                        ? new Date(
                            selectedOrder["Data Effettiva Clienti"],
                          ).toLocaleDateString("it-IT")
                        : "No Data"}
                    </span>
                  </div>
                </div>
              </Col>

              {/* QUANTITÀ KPI */}
              <Col md={6}>
                <div className="h-100 d-flex flex-column justify-content-between">
                  <Row className="g-3 h-100">
                    <Col xs={6}>
                      <div
                        className="p-3 rounded-3 border h-100 d-flex align-items-center justify-content-between bg-white shadow-sm"
                        style={{ borderLeft: "4px solid #495057" }}
                      >
                        <div>
                          <div
                            className="text-muted fw-semibold"
                            style={{
                              fontSize: "0.75rem",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                            }}
                          >
                            Ordinata
                          </div>
                          <div className="h3 fw-bold text-dark mb-0 mt-1">
                            {qtaOrdinata}
                          </div>
                        </div>
                        <div className="p-2 rounded-2 bg-light text-secondary">
                          <Layers size={20} />
                        </div>
                      </div>
                    </Col>

                    <Col xs={6}>
                      <div
                        className="p-3 rounded-3 border h-100 d-flex align-items-center justify-content-between bg-white shadow-sm"
                        style={{ borderLeft: "4px solid #ff922b" }}
                      >
                        <div>
                          <div
                            className="text-warning fw-semibold"
                            style={{
                              fontSize: "0.75rem",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                            }}
                          >
                            Da Evadere
                          </div>
                          <div className="h3 fw-bold text-warning mb-0 mt-1">
                            {qtaDaEvadere}
                          </div>
                        </div>
                        <div
                          className="p-2 rounded-2"
                          style={{
                            backgroundColor: "#fff4e6",
                            color: "#f76707",
                          }}
                        >
                          <Clock size={20} />
                        </div>
                      </div>
                    </Col>

                    <Col xs={6}>
                      <div
                        className="p-3 rounded-3 border h-100 d-flex align-items-center justify-content-between bg-white shadow-sm"
                        style={{ borderLeft: "4px solid #51cf66" }}
                      >
                        <div>
                          <div
                            className="text-success fw-semibold"
                            style={{
                              fontSize: "0.75rem",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                            }}
                          >
                            Evasa (EVA)
                          </div>
                          <div className="h3 fw-bold text-success mb-0 mt-1">
                            {qtaEvasa}
                          </div>
                        </div>
                        <div
                          className="p-2 rounded-2"
                          style={{
                            backgroundColor: "#ebfbee",
                            color: "#37b24d",
                          }}
                        >
                          <CheckCircle size={20} />
                        </div>
                      </div>
                    </Col>

                    <Col xs={6}>
                      <div
                        className="p-3 rounded-3 border h-100 d-flex align-items-center justify-content-between bg-white shadow-sm"
                        style={{ borderLeft: "4px solid #6741d9" }}
                      >
                        <div>
                          <div
                            className="fw-semibold"
                            style={{
                              fontSize: "0.75rem",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              color: "#6741d9",
                            }}
                          >
                            Rimesso (RIM)
                          </div>
                          <div
                            className="h3 fw-bold mb-0 mt-1"
                            style={{ color: "#6741d9" }}
                          >
                            {qtaRimessa}
                          </div>
                        </div>
                        <div
                          className="p-2 rounded-2"
                          style={{
                            backgroundColor: "#f3f0ff",
                            color: "#6741d9",
                          }}
                        >
                          <Layers size={20} />
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          </div>
        </Modal.Body>
      </Modal>{" "}
    </div>
  );
};

export default SalesChart;
